import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ChevronRight, ClipboardList } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Role renk şeması
 */
const COLOR = {
  blue:   { bar: 'bg-blue-500',   ring: 'border-blue-200',  bg: 'bg-blue-50',   text: 'text-blue-700',   btn: 'bg-blue-600 hover:bg-blue-700' },
  green:  { bar: 'bg-green-500',  ring: 'border-green-200', bg: 'bg-green-50',  text: 'text-green-700',  btn: 'bg-green-600 hover:bg-green-700' },
  purple: { bar: 'bg-purple-500', ring: 'border-purple-200',bg: 'bg-purple-50', text: 'text-purple-700', btn: 'bg-purple-600 hover:bg-purple-700' },
  red:    { bar: 'bg-red-500',    ring: 'border-red-300',   bg: 'bg-red-950/40',text: 'text-red-300',    btn: 'bg-red-600 hover:bg-red-700' },
};

/**
 * role: 'exhibitor' | 'contractor' | 'supplier' | 'freelancer'
 * accentColor: 'blue' | 'green' | 'purple' | 'red'
 * token: JWT
 * onboardingUrl: string (e.g. '/app/exhibitor/onboarding')
 * doneKey: localStorage key (e.g. 'exhibitor_onboarding_done')
 * profileApi: string | null — e.g. '/exhibitor/profile-completion'
 * totalSteps: number — onboarding adım sayısı
 */
export default function SetupProgressBanner({ role, accentColor = 'blue', token, onboardingUrl, doneKey, profileApi, totalSteps = 5 }) {
  const navigate = useNavigate();
  const [done, setDone] = useState(null);
  const [total, setTotal] = useState(totalSteps);
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(
    () => !!localStorage.getItem(`${doneKey}_banner_dismissed`)
  );

  const handleDismiss = () => {
    localStorage.setItem(`${doneKey}_banner_dismissed`, '1');
    setDismissed(true);
  };

  const c = COLOR[accentColor] || COLOR.blue;

  useEffect(() => {
    // Eğer localStorage'da done işaretliyse API'ye gitme
    const isDone = localStorage.getItem(doneKey);

    if (profileApi && token) {
      axios.get(`${API}${profileApi}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          const d = r.data;
          setDone(d.done ?? Math.round((d.score / 100) * (d.total ?? totalSteps)));
          setTotal(d.total ?? totalSteps);
          setMissing(d.missing_fields || []);
        })
        .catch(() => {
          // Eğer API yoksa localStorage'a göre karar ver
          setDone(isDone ? totalSteps : 1);
          setTotal(totalSteps);
        })
        .finally(() => setLoading(false));
    } else {
      // API yok — localStorage'a göre
      setDone(isDone ? totalSteps : 1);
      setTotal(totalSteps);
      setLoading(false);
    }
  }, [token, doneKey, profileApi, totalSteps]);

  const pct = done !== null && total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const isComplete = pct >= 100;

  // Tamamlanmışsa veya dismiss edilmişse gösterme
  if (loading || isComplete || dismissed) return null;

  const handleClick = () => {
    localStorage.removeItem(doneKey);
    navigate(onboardingUrl);
  };

  return (
    <div className={`mx-3 mb-3 rounded-xl border ${c.ring} ${c.bg} overflow-hidden`} data-testid="setup-progress-banner">
      {/* Header row */}
      <div className="px-3 pt-3 pb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <ClipboardList className={`h-3.5 w-3.5 shrink-0 ${c.text}`} />
          <span className={`text-xs font-bold ${c.text}`}>Kurulumu Tamamla</span>
        </div>
        <button
          onClick={handleDismiss}
          className="text-gray-400 hover:text-gray-600 text-sm leading-none -mt-0.5"
          aria-label="Kapat"
        >×</button>
      </div>

      {/* Progress bar */}
      <div className="px-3 pb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] text-gray-500">{done}/{total} adım tamamlandı</span>
          <span className={`text-[11px] font-bold ${c.text}`}>{pct}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all duration-500 ${c.bar}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Missing fields (max 2) */}
      {missing.length > 0 && (
        <div className="px-3 pb-2 space-y-0.5">
          {missing.slice(0, 2).map((f, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
              <span className="text-[11px] text-gray-500 truncate">{f}</span>
            </div>
          ))}
          {missing.length > 2 && (
            <span className="text-[11px] text-gray-400 pl-3">+{missing.length - 2} alan daha...</span>
          )}
        </div>
      )}

      {/* CTA button */}
      <div className="px-3 pb-3">
        <button
          onClick={handleClick}
          className={`w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-white ${c.btn} px-3 py-2 rounded-lg transition-colors`}
          data-testid="setup-progress-cta"
        >
          Devam Et <ChevronRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
